// Language strings for the signing workflow — the generated PDF pages, the
// signing page a parent sees, and the email that carries the link.
//
// A packet carries its own language (document_packets.language, 'en' | 'es'),
// set from the client's Language at intake and switchable in the builder, so
// the document, the email and the signing page always match each other.
//
// Spanish here covers AHFC's OWN documents and the app's wording. The DCH
// state appendices are official Georgia forms: they are never re-typeset or
// re-worded, so an appendix DCH publishes only in English stays in English.

export const LANGS = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'es', label: 'Español', short: 'ES' },
];

// Accepts what the packet stores ('es'), what a locale looks like ('es-US'),
// and what the client record holds — clients.language is the English word
// "Spanish", which does NOT start with "es".
export const normalizeLang = (v) => {
  const s = String(v || '').trim().toLowerCase();
  return s.startsWith('es') || s.startsWith('sp') ? 'es' : 'en';
};

// clients.language ("English" / "Spanish") → packet language
export const langFromClient = (client) => normalizeLang(client?.language);

const EN = {
  packetLabel: {
    gapp_intake: 'GAPP Unified Intake Packet',
    gapp_transfer: 'GAPP Transfer',
    private_intake: 'Private Home Care Intake',
  },
  formTitle: {
    phi_release: 'Authorization for Release of Medical Information',
    nacp_signature: 'Assessment, Care Plan & Caregiver Training — Signature Page',
    appendix_n: 'Appendix N — Admission/Continued Stay Checklist',
    appendix_i: 'Appendix I — Letter of Understanding',
    appendix_g: 'Appendix G — Freedom of Choice',
    appendix_h: 'Appendix H — Medical Necessity Statement',
    appendix_u: 'Appendix U — Caregiver Attestation',
    appendix_s: 'Appendix S — Shared Hours Request',
    appendix_v: 'Appendix V — Seizure Observation Log',
    appendix_r: 'Appendix R — GAPP Transfer Request',
  },
  party: {
    client: 'Parent / Caregiver', agency: 'Agency staff', rn: 'Registered Nurse',
    administrator: 'Administrator', witness: 'Witness', physician: 'Physician',
  },
  signerLabel: {
    phi_rep: 'Patient or Authorized Representative',
    phi_witness: 'AHFC Staff Witness',
    nacp_parent: 'Parent / Guardian',
    nacp_rn: 'Registered Nurse',
    i_caregiver: "Member's Caregiver / Primary Caregiver",
    i_agency: 'Nursing Agency Representative',
    g_provider: 'GAPP Provider (Verification)',
    g_member: 'Member (signed by caregiver — minor)',
    g_caregiver: 'Authorized Representative',
    g_witness: 'Witness',
    h_caregiver: 'Primary Caregiver',
    h_agency: 'Nursing Agency',
    u_caregiver: 'Caregiver',
    u_agency: 'GAPP Agency Personnel',
    s_caregiver: 'Caregiver',
    r_caregiver: 'Caregiver',
  },

  // ── PHI Release ──
  phi: {
    title: 'AUTHORIZATION FOR RELEASE OF MEDICAL INFORMATION',
    patientName: 'Patient Name:', dob: 'Date of Birth:', medicaid: 'Medicaid ID:',
    body: 'I, the undersigned, hereby authorize {agency} to request, access, receive, and send the medical records and protected health information (PHI) of the patient named above, from any and all healthcare providers, physicians, specialists, hospitals, pharmacies, therapy providers, and the Georgia Department of Community Health, as necessary for the coordination of home care services and Medicaid prior authorization.',
    revoke: 'I understand this authorization is voluntary and may be revoked in writing at any time. Revocation does not affect disclosures already made.',
    releasedHead: 'INFORMATION TO BE RELEASED (check all that apply)',
    released: {
      'Medical history/diagnoses': 'Medical history/diagnoses',
      'Physician orders/POC': 'Physician orders/POC',
      'Nursing notes': 'Nursing notes',
      Medications: 'Medications',
      'Discharge summaries': 'Discharge summaries',
      'Therapy records': 'Therapy records',
      'Lab/imaging results': 'Lab/imaging results',
      'GAPP documentation': 'GAPP documentation',
    },
    expirationHead: 'EXPIRATION',
    validLabel: 'Authorization valid:',
    validUntilEnd: 'Until end of home care services',
    other: 'Other:',
    iAmThe: 'I am the:',
    signerTypes: {
      'Patient (18+)': 'Patient (18+)',
      'Parent / Legal Guardian': 'Parent / Legal Guardian',
      'Legal Representative / POA': 'Legal Representative / POA',
    },
    signatureHead: 'SIGNATURE',
    sigRep: 'Signature of Patient or Authorized Representative',
    date: 'Date',
    printedName: 'Printed Name',
    relationship: 'Relationship to Patient',
    sigWitness: 'AHFC Staff Witness Signature',
    footer: 'AHFC Authorization for Release of Medical Information  |  HIPAA-Compliant  |  Retain signed copy in client file',
  },

  // ── Assessment / Care Plan / Caregiver Training signature page ──
  nacp: {
    title: 'Comprehensive Assessment, Care Plan & Caregiver Training — Signature Page',
    member: 'Member:', dob: 'DOB:', visitDate: 'Date of Nursing Assessment:',
    parentHead: 'Parent / Guardian Acknowledgement',
    parentIntro: 'I confirm that the Provider RN has discussed the content of this assessment with me in person including:',
    section1: "Section 1 — Nursing Assessment: my child's diagnoses, medical equipment, functional status, respiratory, neurological, skin, and behavioral findings, and the caregiver status and medical necessity summary.",
    section2: 'Section 2 — Nursing Care Plan: the skilled nursing and unskilled care goals, planned interventions, requested service hours, and the safety risk and monitoring plan.',
    section3: 'Section 3 — Caregiver Skills Assessment & Training Plan: the competency audit findings and the training goals and objectives that have been identified for me as the primary caregiver.',
    parentClose: 'I understand that the written report will be completed by the RN following this visit and that my signature below covers the full document as discussed. I have had the opportunity to ask questions and have received satisfactory responses.',
    rnHead: 'Registered Nurse Declaration',
    rnBody: "I certify that I conducted this nursing assessment in person at the member's home on the date shown above, that I verbally discussed the findings and plan with the parent/guardian, and that the written report accurately reflects the clinical assessment, care plan, and caregiver training plan as discussed. This document has been completed to the best of my professional knowledge and in accordance with GAPP program requirements.",
    parentName: 'Parent / Guardian Name', date: 'Date', signature: 'Signature',
    rnName: 'RN Name (print) & License #', rnSignature: 'RN Signature',
    licence: 'Lic. #',
    footer: 'AHFC GAPP Unified Assessment  |  Version 3.0  |  Revised: April 2026  |  Valid 1 year from NA Date. Renew 30–60 days prior per GAPP Policy Manual.',
  },

  // ── Electronic Signature Certificate ──
  cert: {
    title: 'Electronic Signature Certificate',
    intro: "Signing record generated by CAERA, the records system of A Heart For Care. Signatures were adopted electronically under the U.S. ESIGN Act and Georgia UETA: each signer typed their legal name to sign every section they were assigned. Staff signed under their own CAERA login; external signers used a single-use, packet-scoped emailed link that cannot sign any other party's sections. Times are the agency's local time.",
    document: 'Document', member: 'Member', recordId: 'Record ID', status: 'Status',
    created: 'Created', completed: 'Completed', none: '—',
    statusComplete: 'Completed — all {n} sections signed',
    statusPartial: 'In progress — {signed} of {n} sections signed',
    signersHead: 'SIGNERS',
    cols: { signer: 'Signer', role: 'Role', delivered: 'Delivered to', opened: 'Opened', signed: 'Signed', ip: 'IP address' },
    sections: '{signed} of {total} sections',
    inApp: 'Signed in CAERA (own staff login)',
    emailedTo: 'Secure link emailed to {email}',
    sentOn: 'sent {date}',
    emailedLink: 'Secure emailed link',
    linkSent: 'Link sent {date}',
    staffSession: 'staff session',
    unsigned: '— not yet signed —',
    footer: 'CAERA Electronic Signature Certificate',
  },

  // ── Signing page (/sign/:token) ──
  sign: {
    brand: 'A Heart For Care',
    subtitle: 'Document Signing',
    loading: 'Loading…',
    linkUnavailable: 'Link Unavailable',
    linkInvalid: 'This link is invalid or expired.',
    lead: 'Please <b>read the full document below</b>, then type your full legal name to sign your section(s). Your typed name is a legally binding electronic signature (ESIGN Act / UETA).',
    docsToReview: 'Documents to review',
    scrollNote: 'Scroll within the document to read all pages before signing.',
    adoptHead: 'Adopt your signature',
    adoptIntro: 'Type your full legal name once. It will be applied as your electronic signature to every section listed below (legally binding under the ESIGN Act / UETA).',
    namePlaceholder: 'Type your full legal name',
    appliedToHead: 'This signature will be applied to:',
    signAll: 'Adopt & Sign All ({n} {docWord})',
    docWordOne: 'document', docWordMany: 'documents',
    applying: 'Applying signature…',
    needName: 'Type your full legal name to adopt your signature.',
    signFailed: 'Could not complete signing: {msg}',
    doneBanner: '✓ Thank you — your signature has been applied to all your sections. A Heart For Care will finalize the documents.',
    appliedTo: 'Applied to:',
    questions: 'Questions? Contact',
    // guided page
    hello: 'Hello {name},',
    intro: 'A Heart For Care has {n} {docWord} for {patient} for you to sign. You will be taken through them one at a time — {sigs} places to sign{choices}. It takes about five minutes.',
    introChoices: ' and one choice to make',
    start: 'Start',
    sigWordOne: 'signature', sigWordMany: 'signatures',
    docOf: 'Document {i} of {n}',
    readAll: 'Read every page',
    signHere: 'Sign here',
    signed: 'Signed',
    signSpot: 'Your signature — {label}',
    signAllRemaining: 'Sign all remaining ({n})',
    nextDoc: 'Next document →',
    back: '← Back',
    finish: 'Finish',
    allDone: 'All done',
    finishSummary: 'You signed {sigs} places across {n} {docWord}. A Heart For Care will file the documents and send you nothing further unless something is needed.',
    adoptTitle: 'Your signature',
    adoptBody: 'Type your full legal name. It becomes your electronic signature and is applied wherever you tap Sign here (legally binding under the ESIGN Act / UETA).',
    adoptApply: 'Use this signature',
    phiHead: 'Choose what may be shared',
    phiIntro: 'This authorization lets A Heart For Care request and send {patient}\u2019s medical records for care coordination and Medicaid authorization. Tick what may be shared, how long it lasts, and who you are.',
    phiValidLabel: 'This authorization is valid',
    phiValidOtherLabel: 'Until (date or condition)',
    phiSignerLabel: 'I am the',
    saved: 'Saved',
    nothingToSign: 'Nothing for you to sign on this document — it is here for you to read.',
    // The LON's question, answered before signing it (lib/documents/lonLayout).
    agreeHead: 'Do you agree with the hours in this letter?',
    agreeIntro: 'Your answer is marked on the letter with your signature. If you disagree, you can ask DCH to reconsider — we will help you with it.',
    agree: 'I agree',
    disagree: 'I disagree',
    agreeFirst: 'Please answer whether you agree or disagree with the hours before signing this letter.',
    remaining: '{n} left',
  },

  // ── Email carrying the signing link ──
  email: {
    subject: 'Documents to sign — {patient} — A Heart For Care',
    headerTitle: 'A Heart For Care',
    headerSub: 'Document Signing',
    greeting: 'Hello,',
    body: 'Documents for <strong>{patient}</strong> are ready for your review and signature. Please click the button below to open the secure signing page — you will only see the sections that require your signature.',
    button: 'Review &amp; Sign Documents',
    fallback: "If the button doesn't work, copy and paste this URL into your browser:",
    footer: 'A Heart For Care · AHFC CRM · This is a secure, automated message',
  },
};

const ES = {
  packetLabel: {
    gapp_intake: 'Paquete unificado de admisión de GAPP',
    gapp_transfer: 'Transferencia de GAPP',
    private_intake: 'Admisión de cuidado privado en el hogar',
  },
  formTitle: {
    phi_release: 'Autorización para divulgar información médica',
    nacp_signature: 'Evaluación, plan de cuidado y capacitación del cuidador — Página de firmas',
    appendix_n: 'Apéndice N — Lista de verificación de admisión o continuación',
    appendix_i: 'Apéndice I — Carta de entendimiento',
    appendix_g: 'Apéndice G — Libertad de elección',
    appendix_h: 'Apéndice H — Declaración de necesidad médica',
    appendix_u: 'Apéndice U — Declaración del cuidador',
    appendix_s: 'Apéndice S — Solicitud de horas compartidas',
    appendix_v: 'Apéndice V — Registro de observación de convulsiones',
    appendix_r: 'Apéndice R — Solicitud de transferencia de GAPP',
  },
  party: {
    client: 'Padre/madre o cuidador', agency: 'Personal de la agencia', rn: 'Enfermera registrada',
    administrator: 'Administrador', witness: 'Testigo', physician: 'Médico',
  },
  signerLabel: {
    phi_rep: 'Paciente o representante autorizado',
    phi_witness: 'Testigo, personal de AHFC',
    nacp_parent: 'Padre, madre o tutor',
    nacp_rn: 'Enfermera registrada',
    i_caregiver: 'Cuidador del miembro / cuidador principal',
    i_agency: 'Representante de la agencia de enfermería',
    g_provider: 'Proveedor de GAPP (verificación)',
    g_member: 'Miembro (firma el cuidador — menor de edad)',
    g_caregiver: 'Representante autorizado',
    g_witness: 'Testigo',
    h_caregiver: 'Cuidador principal',
    h_agency: 'Agencia de enfermería',
    u_caregiver: 'Cuidador',
    u_agency: 'Personal de la agencia de GAPP',
    s_caregiver: 'Cuidador',
    r_caregiver: 'Cuidador',
  },

  phi: {
    title: 'AUTORIZACIÓN PARA DIVULGAR INFORMACIÓN MÉDICA',
    patientName: 'Nombre del paciente:', dob: 'Fecha de nacimiento:', medicaid: 'N.º de Medicaid:',
    body: 'Yo, la persona abajo firmante, autorizo a {agency} a solicitar, consultar, recibir y enviar los expedientes médicos y la información de salud protegida (PHI) del paciente indicado arriba, de cualquier proveedor de servicios de salud, médico, especialista, hospital, farmacia o proveedor de terapias, y del Departamento de Salud Comunitaria de Georgia, según sea necesario para coordinar los servicios de cuidado en el hogar y la autorización previa de Medicaid.',
    revoke: 'Entiendo que esta autorización es voluntaria y que puedo revocarla por escrito en cualquier momento. La revocación no afecta la información que ya se haya divulgado.',
    releasedHead: 'INFORMACIÓN QUE SE DIVULGARÁ (marque todo lo que corresponda)',
    released: {
      'Medical history/diagnoses': 'Historial médico y diagnósticos',
      'Physician orders/POC': 'Órdenes médicas y plan de cuidado',
      'Nursing notes': 'Notas de enfermería',
      Medications: 'Medicamentos',
      'Discharge summaries': 'Resúmenes de alta',
      'Therapy records': 'Registros de terapia',
      'Lab/imaging results': 'Resultados de laboratorio e imágenes',
      'GAPP documentation': 'Documentación del programa GAPP',
    },
    expirationHead: 'VIGENCIA',
    validLabel: 'Autorización válida:',
    validUntilEnd: 'Hasta el final de los servicios de cuidado en el hogar',
    other: 'Otro:',
    iAmThe: 'Yo soy:',
    signerTypes: {
      'Patient (18+)': 'El paciente (mayor de 18 años)',
      'Parent / Legal Guardian': 'Padre, madre o tutor legal',
      'Legal Representative / POA': 'Representante legal / poder notarial',
    },
    signatureHead: 'FIRMA',
    sigRep: 'Firma del paciente o representante autorizado',
    date: 'Fecha',
    printedName: 'Nombre en letra de molde',
    relationship: 'Parentesco con el paciente',
    sigWitness: 'Firma del testigo, personal de AHFC',
    footer: 'Autorización de AHFC para divulgar información médica  |  Conforme a HIPAA  |  Conserve la copia firmada en el expediente del cliente',
  },

  nacp: {
    title: 'Evaluación integral, plan de cuidado y capacitación del cuidador — Página de firmas',
    member: 'Miembro:', dob: 'Fecha de nacimiento:', visitDate: 'Fecha de la evaluación de enfermería:',
    parentHead: 'Reconocimiento del padre, madre o tutor',
    parentIntro: 'Confirmo que la enfermera registrada del proveedor conversó conmigo en persona sobre el contenido de esta evaluación, incluyendo:',
    section1: 'Sección 1 — Evaluación de enfermería: los diagnósticos de mi hijo o hija, el equipo médico, el estado funcional, los hallazgos respiratorios, neurológicos, de la piel y del comportamiento, y el resumen de la situación del cuidador y de la necesidad médica.',
    section2: 'Sección 2 — Plan de cuidado de enfermería: las metas de cuidado especializado y no especializado, las intervenciones planificadas, las horas de servicio solicitadas y el plan de riesgos de seguridad y monitoreo.',
    section3: 'Sección 3 — Evaluación de destrezas del cuidador y plan de capacitación: los resultados de la revisión de competencias y las metas y objetivos de capacitación identificados para mí como cuidador principal.',
    parentClose: 'Entiendo que la enfermera registrada completará el informe escrito después de esta visita y que mi firma a continuación abarca el documento completo tal como se conversó. Tuve la oportunidad de hacer preguntas y recibí respuestas satisfactorias.',
    rnHead: 'Declaración de la enfermera registrada',
    rnBody: 'Certifico que realicé esta evaluación de enfermería en persona en el hogar del miembro en la fecha indicada arriba, que conversé verbalmente los hallazgos y el plan con el padre, la madre o el tutor, y que el informe escrito refleja con exactitud la evaluación clínica, el plan de cuidado y el plan de capacitación del cuidador tal como se conversó. Este documento se completó según mi mejor conocimiento profesional y conforme a los requisitos del programa GAPP.',
    parentName: 'Nombre del padre, madre o tutor', date: 'Fecha', signature: 'Firma',
    rnName: 'Nombre de la enfermera (letra de molde) y n.º de licencia', rnSignature: 'Firma de la enfermera',
    licence: 'Lic. n.º',
    footer: 'Evaluación unificada GAPP de AHFC  |  Versión 3.0  |  Revisada: abril de 2026  |  Válida 1 año desde la fecha de la evaluación. Renovar de 30 a 60 días antes, según el manual de políticas de GAPP.',
  },

  cert: {
    title: 'Certificado de firma electrónica',
    intro: 'Registro de firmas generado por CAERA, el sistema de expedientes de A Heart For Care. Las firmas se adoptaron electrónicamente conforme a la Ley ESIGN de los Estados Unidos y la Ley UETA de Georgia: cada firmante escribió su nombre legal para firmar todas las secciones que le correspondían. El personal firmó con su propia cuenta de CAERA; los firmantes externos usaron un enlace de un solo uso, limitado a este paquete, que no puede firmar las secciones de ninguna otra parte. Las horas corresponden a la zona horaria local de la agencia.',
    document: 'Documento', member: 'Miembro', recordId: 'N.º de registro', status: 'Estado',
    created: 'Creado', completed: 'Completado', none: '—',
    statusComplete: 'Completado — se firmaron las {n} secciones',
    statusPartial: 'En curso — se firmaron {signed} de {n} secciones',
    signersHead: 'FIRMANTES',
    cols: { signer: 'Firmante', role: 'Función', delivered: 'Enviado a', opened: 'Abierto', signed: 'Firmado', ip: 'Dirección IP' },
    sections: '{signed} de {total} secciones',
    inApp: 'Firmado en CAERA (con su propia cuenta)',
    emailedTo: 'Enlace seguro enviado a {email}',
    sentOn: 'enviado el {date}',
    emailedLink: 'Enlace seguro por correo',
    linkSent: 'Enlace enviado el {date}',
    staffSession: 'sesión del personal',
    unsigned: '— sin firmar —',
    footer: 'Certificado de firma electrónica de CAERA',
  },

  sign: {
    brand: 'A Heart For Care',
    subtitle: 'Firma de documentos',
    loading: 'Cargando…',
    linkUnavailable: 'Enlace no disponible',
    linkInvalid: 'Este enlace no es válido o ya venció.',
    lead: 'Por favor <b>lea el documento completo que aparece abajo</b> y luego escriba su nombre legal completo para firmar la sección o las secciones que le corresponden. Su nombre escrito es una firma electrónica con validez legal (Ley ESIGN / UETA).',
    docsToReview: 'Documentos para revisar',
    scrollNote: 'Desplácese dentro del documento para leer todas las páginas antes de firmar.',
    adoptHead: 'Adopte su firma',
    adoptIntro: 'Escriba una sola vez su nombre legal completo. Se aplicará como su firma electrónica en todas las secciones indicadas abajo (con validez legal conforme a la Ley ESIGN / UETA).',
    namePlaceholder: 'Escriba su nombre legal completo',
    appliedToHead: 'Esta firma se aplicará a:',
    signAll: 'Adoptar y firmar todo ({n} {docWord})',
    docWordOne: 'documento', docWordMany: 'documentos',
    applying: 'Aplicando la firma…',
    needName: 'Escriba su nombre legal completo para adoptar su firma.',
    signFailed: 'No se pudo completar la firma: {msg}',
    hello: 'Hola {name}:',
    intro: 'A Heart For Care tiene {n} {docWord} de {patient} para su firma. Se le guiará por cada uno: {sigs} lugares para firmar{choices}. Toma unos cinco minutos.',
    introChoices: ' y una decisión que tomar',
    start: 'Comenzar',
    sigWordOne: 'firma', sigWordMany: 'firmas',
    docOf: 'Documento {i} de {n}',
    readAll: 'Leer todas las páginas',
    signHere: 'Firme aquí',
    signed: 'Firmado',
    signSpot: 'Su firma — {label}',
    signAllRemaining: 'Firmar todo lo restante ({n})',
    nextDoc: 'Siguiente documento →',
    back: '← Atrás',
    finish: 'Terminar',
    allDone: 'Listo',
    finishSummary: 'Firmó en {sigs} lugares de {n} {docWord}. A Heart For Care archivará los documentos y no le enviará nada más a menos que haga falta algo.',
    adoptTitle: 'Su firma',
    adoptBody: 'Escriba su nombre legal completo. Se convierte en su firma electrónica y se aplica donde toque «Firme aquí» (con validez legal conforme a la Ley ESIGN / UETA).',
    adoptApply: 'Usar esta firma',
    phiHead: 'Elija qué información puede compartirse',
    phiIntro: 'Esta autorización permite a A Heart For Care solicitar y enviar los registros médicos de {patient} para coordinar el cuidado y la autorización de Medicaid. Marque qué puede compartirse, por cuánto tiempo y quién es usted.',
    phiValidLabel: 'Esta autorización es válida',
    phiValidOtherLabel: 'Hasta (fecha o condición)',
    phiSignerLabel: 'Yo soy',
    saved: 'Guardado',
    nothingToSign: 'No hay nada que firmar en este documento; está aquí para que lo lea.',
    agreeHead: '¿Está de acuerdo con las horas de esta carta?',
    agreeIntro: 'Su respuesta se marca en la carta junto con su firma. Si no está de acuerdo, puede pedir al DCH que lo reconsidere; le ayudaremos con ello.',
    agree: 'Estoy de acuerdo',
    disagree: 'No estoy de acuerdo',
    agreeFirst: 'Antes de firmar esta carta, indique si está de acuerdo o no con las horas.',
    remaining: 'faltan {n}',
    doneBanner: '✓ Gracias. Su firma se aplicó a todas sus secciones. A Heart For Care finalizará los documentos.',
    appliedTo: 'Aplicada a:',
    questions: '¿Tiene preguntas? Escriba a',
  },

  email: {
    subject: 'Documentos para firmar — {patient} — A Heart For Care',
    headerTitle: 'A Heart For Care',
    headerSub: 'Firma de documentos',
    greeting: 'Hola:',
    body: 'Los documentos de <strong>{patient}</strong> están listos para que usted los revise y los firme. Haga clic en el botón de abajo para abrir la página segura de firma. Solo verá las secciones que requieren su firma.',
    button: 'Revisar y firmar documentos',
    fallback: 'Si el botón no funciona, copie y pegue esta dirección en su navegador:',
    footer: 'A Heart For Care · Este es un mensaje automático y seguro',
  },
};

const DICT = { en: EN, es: ES };

// t('es') → strings for that language, with a fill() helper for {placeholders}.
export function t(lang) {
  return DICT[normalizeLang(lang)] || EN;
}

export function fill(template, vars = {}) {
  return String(template).replace(/\{(\w+)\}/g, (m, k) => (vars[k] === undefined ? m : String(vars[k])));
}

// Form title in the packet's language, falling back to whatever the DB stored.
export const formTitle = (lang, formKey, fallback) => t(lang).formTitle[formKey] || fallback || formKey;
export const packetLabel = (lang, type, fallback) => t(lang).packetLabel[type] || fallback || type;
export const signerLabel = (lang, slotKey, fallback) => t(lang).signerLabel[slotKey] || fallback || slotKey;
